import { Copy as CopyIcon, Locked as LockedIcon, Unlocked as UnlockIcon, View as ViewIcon } from '@carbon/icons-react';
import type { ActionsOverlayAction } from 'component/actions-overlay';
import { ActionsOverlay } from 'component/actions-overlay';
import { colorTextWarning } from 'component/color';
import { addNotificationToast } from 'component/notification-toast';
import { useDatastore } from 'util/datastore';
import { makeUrlForInstance } from 'util/navigate';
import { t } from 'util/stdtypes';

export type QuestionInstance = {
    structureKey: string;
    instanceKey: string;
};

type QuestionActionsOverlayAction = ActionsOverlayAction<{ question: QuestionInstance }>;

export const ActionPreview: QuestionActionsOverlayAction = {
    label: t('Preview'),
    icon: ViewIcon,
    action: async ({ datastore, question }) => {
        return datastore.gotoInstance({
            structureKey: question.structureKey,
            instanceKey: question.instanceKey,
        });
    },
};

export const ActionCopy: QuestionActionsOverlayAction = {
    label: t('Copy'),
    icon: CopyIcon,
    action: async ({ datastore, question }) => {
        const url = makeUrlForInstance({ structureKey: question.structureKey, instanceKey: question.instanceKey });
        navigator.clipboard.writeText(url);
        if (datastore.props.appData) {
            addNotificationToast({ appData: datastore.props.appData, type: 'success', label: t('Link copied to clipboard!') });
        }
    },
};

export const ActionClose: QuestionActionsOverlayAction = {
    label: t('Close'),
    icon: LockedIcon,
    color: colorTextWarning,
    action: async ({ datastore }) => {
        const features = datastore.getGlobalProperty('features') ?? {};
        const newFeatures = {
            ...features,
            readonly: true,
        };
        datastore.setGlobalProperty('features', newFeatures);
    },
    enabled: ({ features }) => {
        return !features?.readonly;
    },
    modal: {
        headingLabel: t('Close this space?'),
        descriptionLabel: t(
            'When closed, a space becomes “read only.” All actions are disabled and users cannot post, reply, react, report, etc. You can reopen the conversation at any time.',
        ),
        confirmButton: {
            label: t('Close'),
            icon: <LockedIcon color={colorTextWarning} />,
            type: 'delete',
        },
    },
};

export const ActionOpen: QuestionActionsOverlayAction = {
    label: t('Re-open'),
    icon: UnlockIcon,
    color: colorTextWarning,
    action: async ({ datastore }) => {
        const features = datastore.getGlobalProperty('features') ?? {};
        const newFeatures = {
            ...features,
            readonly: false,
        };
        datastore.setGlobalProperty('features', newFeatures);
    },
    enabled: ({ features }) => {
        return features?.readonly;
    },
    modal: {
        headingLabel: t('Re-open this space?'),
        descriptionLabel: t('Re-open this space to make the conversation active again.'),
        confirmButton: {
            label: t('Re-open'),
            icon: <UnlockIcon color={colorTextWarning} />,
            type: 'delete',
        },
    },
};

export function QuestionActionsOverlay() {
    const datastore = useDatastore();
    const features = datastore.getGlobalProperty('features') ?? {};
    const availableActions = [ActionPreview, ActionCopy, ActionClose, ActionOpen];
    const actions = availableActions.filter((action) => (action.enabled ? action.enabled({ features }) : true));
    return (
        <ActionsOverlay
            actionArgs={{ question: { structureKey: 'question', instanceKey: datastore.getInstanceKey() } }}
            actions={actions}
        />
    );
}
